import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowUpRight } from "lucide-react";
import { CONSULTATIONS } from "../data/consultations";

gsap.registerPlugin(ScrollTrigger);

const Panel = ({ s, index, active, onToggle }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(1200px) rotateY(${x * 3}deg) rotateX(${-y * 3}deg)`;
  };
  const onLeave = () => {
    if (ref.current) ref.current.style.transform = "perspective(1200px) rotateY(0) rotateX(0)";
  };

  return (
    <article
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onClick={() => onToggle(index)}
      data-testid={`service-panel-${index}`}
      style={{
        boxShadow: active
          ? "0 0 0 1px rgba(110,91,255,0.35), 0 30px 80px -30px rgba(0,224,255,0.25)"
          : "none",
      }}
      className="relative shrink-0 isolate overflow-hidden w-[82vw] md:w-[44vw] lg:w-[34vw] h-[64vh] md:h-[68vh] bg-surface hairline rounded-[2px] p-7 md:p-10 flex flex-col justify-between cursor-pointer group transition-shadow duration-500 will-change-transform"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: "radial-gradient(60% 50% at 50% 0%, rgba(110,91,255,0.10), transparent 70%)",
        }}
      />
      <div className="relative flex items-start justify-between">
        <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">{s.n} · {s.category}</span>
        <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">
          {active ? "— Collapse" : "Expand +"}
        </span>
      </div>

      <div className="relative">
        <h3
          className="font-display font-medium leading-[1.05] tracking-tight text-[#0a0a0b] transition-transform duration-500 group-hover:-translate-y-1 break-words"
          style={{ fontSize: "clamp(1.5rem, 2.8vw, 2.6rem)" }}
        >
          {s.title}
        </h3>
        <p className="mt-3 text-sm md:text-base accent-text font-medium">{s.tagline}</p>
        <p className="mt-3 text-xs md:text-sm text-muted-ink leading-relaxed line-clamp-4">{s.body}</p>

        <div
          className="grid grid-cols-1 gap-2 overflow-hidden transition-[max-height,opacity,margin] duration-700 ease-out"
          style={{
            maxHeight: active ? "260px" : "0",
            opacity: active ? 1 : 0,
            marginTop: active ? "1rem" : "0",
          }}
          data-testid={`service-panel-${index}-detail`}
        >
          {s.bullets.slice(0, 4).map((b) => (
            <div key={b} className="hairline rounded-[2px] px-3 py-1.5 text-[11px] text-[#0a0a0b] truncate">
              {b}
            </div>
          ))}
        </div>

        <div className="mt-5">
          <Link
            to={`/services/${s.slug}`}
            data-testid={`service-panel-${index}-link`}
            className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#0a0a0b] hover:accent-text transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            Explore <ArrowUpRight size={12} />
          </Link>
        </div>
      </div>
    </article>
  );
};

const ConsultationsPreview = () => {
  const sectionRef = useRef(null);
  const stickyRef = useRef(null);
  const trackRef = useRef(null);
  const [active, setActive] = useState(-1);

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!isDesktop || reduce) return;

    const ctx = gsap.context(() => {
      const track = trackRef.current;
      const section = sectionRef.current;
      if (!track || !section) return;
      const totalScroll = track.scrollWidth - window.innerWidth;
      section.style.height = `${window.innerHeight + totalScroll + 200}px`;

      gsap.to(track, {
        x: -totalScroll,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: () => `+=${totalScroll + 200}`,
          scrub: 1,
          invalidateOnRefresh: true,
        },
      });
    }, sectionRef);

    return () => {
      try {
        ScrollTrigger.getAll().forEach((st) => {
          if (st.vars && st.vars.trigger === sectionRef.current) st.kill();
        });
        ctx.revert();
        if (sectionRef.current) sectionRef.current.style.height = "";
      } catch (e) {
        /* swallow */
      }
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      data-testid="services-section"
      id="services"
      className="relative py-24 md:py-28 lg:py-0 lg:pt-24"
    >
      <div className="px-6 md:px-12 lg:px-20 mb-12 lg:mb-16 flex items-end justify-between flex-wrap gap-6">
        <div className="flex items-center gap-4">
          <span className="h-px w-12 bg-[#0a0a0b]/30" />
          <span className="text-[10px] tracking-[0.4em] uppercase text-muted-ink">02 — Services</span>
        </div>
        <div className="flex items-end gap-6 flex-wrap">
          <h2
            className="font-display font-medium tracking-tight leading-none text-[#0a0a0b]"
            style={{ fontSize: "clamp(2rem, 4.5vw, 4rem)" }}
            data-testid="services-headline"
          >
            Five disciplines. <span className="accent-text">One studio.</span>
          </h2>
          <Link
            to="/services"
            data-testid="services-view-all"
            className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[#0a0a0b] hairline-strong px-4 py-2 rounded-full hover:accent-text transition-colors"
          >
            View all <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>

      <div
        ref={stickyRef}
        className="relative lg:sticky lg:top-0 lg:h-screen flex items-center overflow-hidden"
      >
        <div
          ref={trackRef}
          className="flex gap-6 md:gap-8 px-6 md:px-12 lg:px-20 lg:flex-nowrap flex-wrap lg:overflow-visible"
        >
          {CONSULTATIONS.map((s, i) => (
            <Panel
              key={s.slug}
              s={s}
              index={i}
              active={active === i}
              onToggle={(idx) => setActive(active === idx ? -1 : idx)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ConsultationsPreview;
